/**
 * ConnectChannelDialog — channel-aware modal that walks the user through the
 * verification flow for the chosen channel and reports back a LinkResponse.
 */

import { useEffect, useRef, useState } from "react";
import { KeyRound, Loader2, ShieldOff, UserRound } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  externalIdentityService,
  type LinkResponse,
  type TelegramLoginPayload,
} from "@/services/external-identity-service";

interface ConnectChannelDialogProps {
  channel: string;
  onClose: () => void;
  onLinked: (response: LinkResponse) => void;
}

export function ConnectChannelDialog({ channel, onClose, onLinked }: ConnectChannelDialogProps) {
  const titles: Record<string, string> = {
    telegram: "Connect Telegram",
    slack: "Connect Slack",
    teams: "Connect Microsoft Teams",
    whatsapp: "Connect WhatsApp",
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{titles[channel] || "Connect account"}</DialogTitle>
          <DialogDescription>
            Verify ownership so messages from this account route to your TeamMate user.
          </DialogDescription>
        </DialogHeader>

        {channel === "telegram" ? (
          <TelegramBody onLinked={onLinked} />
        ) : channel === "slack" ? (
          <SlackBody onLinked={onLinked} />
        ) : channel === "teams" ? (
          <TeamsBody onLinked={onLinked} />
        ) : channel === "whatsapp" ? (
          <PhoneBody onLinked={onLinked} />
        ) : (
          <div className="text-sm text-muted-foreground">This channel is not yet supported.</div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========== Telegram ==========

const TELEGRAM_WIDGET_SRC = "https://telegram.org/js/telegram-widget.js?22";

function TelegramBody({ onLinked }: { onLinked: (r: LinkResponse) => void }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Register the global callback the Telegram script expects to find on window.
    const callbackName = `onTelegramAuth_${Date.now()}`;
    (window as unknown as Record<string, unknown>)[callbackName] = async (
      payload: TelegramLoginPayload,
    ) => {
      setLinking(true);
      setError(null);
      try {
        const response = await externalIdentityService.linkTelegram(payload);
        onLinked(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verification failed");
      } finally {
        setLinking(false);
      }
    };

    const botUsername = import.meta.env.VITE_TELEGRAM_LOGIN_BOT_USERNAME as string | undefined;
    if (!botUsername) {
      setError(
        "Telegram linking is not configured. Set VITE_TELEGRAM_LOGIN_BOT_USERNAME in the client env.",
      );
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = TELEGRAM_WIDGET_SRC;
    script.setAttribute("data-telegram-login", botUsername);
    script.setAttribute("data-size", "large");
    script.setAttribute("data-userpic", "true");
    script.setAttribute("data-request-access", "write");
    script.setAttribute("data-onauth", `${callbackName}(user)`);
    container.appendChild(script);

    return () => {
      delete (window as unknown as Record<string, unknown>)[callbackName];
      container.replaceChildren();
    };
  }, [onLinked]);

  return (
    <div className="space-y-3 py-2">
      <p className="text-sm text-muted-foreground">
        Click the button below to authenticate with Telegram. We never see your password —
        Telegram returns a signed payload that we verify server-side.
      </p>
      <div ref={containerRef} className="flex justify-center" />
      {linking ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Verifying…
        </div>
      ) : null}
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  );
}

// ========== Slack ==========

function SlackBody({ onLinked }: { onLinked: (r: LinkResponse) => void }) {
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConnect = async () => {
    setLinking(true);
    setError(null);
    try {
      const { url, state } = await externalIdentityService.startSlackOAuth();

      // Open Slack's consent screen in a popup. Wait for the server-rendered
      // callback-relay page to post the code back via window.postMessage.
      const popup = window.open(url, "slack-link", "width=520,height=720");
      if (!popup) {
        throw new Error("Popup blocked. Allow popups for this site to link Slack.");
      }

      const code = await waitForOAuthCode(state, popup);
      const response = await externalIdentityService.finishSlackOAuth({ code, state });
      onLinked(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Slack linking failed");
    } finally {
      setLinking(false);
    }
  };

  return (
    <div className="space-y-3 py-2">
      <p className="text-sm text-muted-foreground">
        We use Slack&apos;s &ldquo;Sign in with Slack&rdquo; to identify your user across the workspace.
        No bot install required.
      </p>
      <Button onClick={handleConnect} disabled={linking} className="w-full">
        {linking ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
        Sign in with Slack
      </Button>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  );
}

/**
 * Waits for the Slack callback-relay popup to post the OAuth `code + state`
 * back via window.postMessage. We do NOT enforce `event.origin` because the
 * relay lives on the API origin which can differ from the SPA origin (e.g.
 * api.tmmate.ai vs tmmate.ai). Integrity comes from two checks:
 *   - `data.source === "slack-link"` — narrows to our relay
 *   - `data.state === expectedState` — random per-request nonce a third
 *     party can't guess, even if they postMessage the opener
 */
function waitForOAuthCode(expectedState: string, popup: Window): Promise<string> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener("message", handler);
      window.clearTimeout(timeout);
      window.clearInterval(watchdog);
    };

    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error("Slack linking timed out"));
    }, 120_000);

    function handler(event: MessageEvent) {
      const data = event.data as { source?: string; code?: string; state?: string; error?: string } | null;
      if (!data || data.source !== "slack-link") return;
      cleanup();
      if (data.error) reject(new Error(data.error));
      else if (!data.code) reject(new Error("Slack did not return a code"));
      else if (data.state !== expectedState) reject(new Error("Slack state mismatch"));
      else resolve(data.code);
    }

    window.addEventListener("message", handler);
    const watchdog = window.setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new Error("Popup closed before completion."));
      }
    }, 1000);
  });
}

// ========== Phone OTP (unified WhatsApp linking) ==========

function PhoneBody({ onLinked }: { onLinked: (r: LinkResponse) => void }) {
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [verificationId, setVerificationId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await externalIdentityService.startPhoneOtp({ phone });
      setVerificationId(result.verificationId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send code");
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    if (!verificationId) return;
    setBusy(true);
    setError(null);
    try {
      const response = await externalIdentityService.verifyPhoneOtp({
        verificationId,
        code,
      });
      onLinked(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 py-2">
      <div className="space-y-1.5">
        <Label htmlFor="connect-phone">Phone number (E.164)</Label>
        <Input
          id="connect-phone"
          placeholder="+97450123456"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={busy || !!verificationId}
        />
      </div>

      {verificationId ? (
        <div className="space-y-1.5">
          <Label htmlFor="connect-otp">Verification code</Label>
          <Input
            id="connect-otp"
            placeholder="123456"
            value={code}
            inputMode="numeric"
            maxLength={6}
            onChange={(e) => setCode(e.target.value)}
            disabled={busy}
          />
          <p className="text-xs text-muted-foreground">
            We sent a 6-digit code to {phone}. It expires in 10 minutes.
          </p>
        </div>
      ) : null}

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <div className="flex justify-end">
        {verificationId ? (
          <Button onClick={verifyCode} disabled={busy || code.length < 4}>
            {busy ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
            Verify
          </Button>
        ) : (
          <Button onClick={sendCode} disabled={busy || phone.trim().length < 5}>
            {busy ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
            Send code
          </Button>
        )}
      </div>
    </div>
  );
}

// ========== Microsoft Teams ==========

function TeamsBody({ onLinked }: { onLinked: (r: LinkResponse) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConnect = async () => {
    setBusy(true);
    setError(null);
    try {
      const { authUrl, state, nonce } = await externalIdentityService.startTeamsOAuth();

      const popup = window.open(authUrl, "teams-link", "width=520,height=720");
      if (!popup) {
        throw new Error("Popup blocked. Allow popups for this site to connect Teams.");
      }

      const code = await waitForMicrosoftRelay(popup, state);
      const response = await externalIdentityService.finishTeamsOAuth({ code, nonce });
      onLinked(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not connect Teams");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3 py-2">
      <p className="text-sm text-muted-foreground">
        Sign in with your Microsoft work account to link your Teams identity so
        DMs to the agent route to you.
      </p>

      <div>
        <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          What we&apos;ll access
        </div>
        <div className="grid grid-cols-1 gap-1.5">
          {TEAMS_SCOPE_PREVIEW.map((g) => {
            const Icon = g.icon;
            return (
              <div
                key={g.key}
                className="flex items-start gap-2 rounded-md border border-border bg-background px-2 py-1.5"
              >
                <div
                  className={`flex size-7 shrink-0 items-center justify-center rounded ${g.tileClass}`}
                >
                  <Icon className={`size-3.5 ${g.iconClass}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-foreground leading-tight">
                    {g.title}
                  </div>
                  <div className="text-[10px] text-muted-foreground leading-snug mt-0.5">
                    {g.subtitle}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Button onClick={handleConnect} disabled={busy} className="w-full">
        {busy ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
        Sign in with Microsoft
      </Button>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
    </div>
  );
}

/**
 * Plain-language preview of the Teams link scopes
 * (`openid profile offline_access User.Read`). Mirrors the scope-group tiles
 * in ConnectMicrosoftDialog — but here the set is intentionally tiny: just
 * enough to identify the user, nothing that touches their data.
 */
const TEAMS_SCOPE_PREVIEW = [
  {
    key: "profile",
    icon: UserRound,
    iconClass: "text-blue-600 dark:text-blue-400",
    tileClass: "bg-blue-100 dark:bg-blue-900/30",
    title: "Your basic profile",
    subtitle: "Name and email — so the agent recognizes you in Teams",
  },
  {
    key: "session",
    icon: KeyRound,
    iconClass: "text-slate-600 dark:text-slate-400",
    tileClass: "bg-slate-100 dark:bg-slate-900/30",
    title: "Stay connected",
    subtitle: "Keeps the link valid without asking you to sign in again",
  },
  {
    key: "nothing-else",
    icon: ShieldOff,
    iconClass: "text-emerald-600 dark:text-emerald-400",
    tileClass: "bg-emerald-100 dark:bg-emerald-900/30",
    title: "Nothing else",
    subtitle: "No mail, calendar, files, or chat history",
  },
] as const;

/**
 * Waits for the Microsoft OAuth callback popup to relay `code + state` back
 * via window.postMessage (the server-rendered callback page posts a
 * `{ source: "taqat-teams-oauth", ok, code, state, message }` payload), then
 * resolves the code after the caller-supplied CSRF `state` matches.
 */
function waitForMicrosoftRelay(popup: Window, expectedState: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener("message", handler);
      clearInterval(watchdog);
    };

    function handler(event: MessageEvent) {
      const data = event.data as {
        source?: string;
        ok?: boolean;
        code?: string | null;
        state?: string | null;
        message?: string | null;
      } | null;
      if (!data || data.source !== "taqat-teams-oauth") return;
      cleanup();
      if (!data.ok) {
        reject(new Error(data.message || "Microsoft connection failed."));
        return;
      }
      if (!data.code || data.state !== expectedState) {
        reject(new Error("Verification failed — state mismatch."));
        return;
      }
      resolve(data.code);
    }

    window.addEventListener("message", handler);
    const watchdog = setInterval(() => {
      if (popup.closed) {
        cleanup();
        reject(new Error("Popup closed before completion."));
      }
    }, 1000);
  });
}
